import createClient, { type Middleware } from "openapi-fetch";
import type { paths } from "./schema";

// ProblemDetail は OpenAPI で定義された RFC 7807 形式に準拠する。
// schema.d.ts の生成型と独立に最小スキーマで宣言しているのは、
// エラー応答の経路を型レベルでも常に同一形状として扱いたいため。
export type ProblemDetail = {
  type: string;
  title: string;
  status: number;
  detail: string;
  reason?: string;
  errors?: Array<{ field: string; message: string }>;
};

export class ApiError extends Error {
  readonly status: number;
  readonly problem: ProblemDetail;

  constructor(status: number, problem: ProblemDetail) {
    super(problem.title);
    this.name = "ApiError";
    this.status = status;
    this.problem = problem;
  }
}

// 本物の ProblemDetail でない応答（テキスト 500 など）にも下流が同じ形で
// 触れられるよう、最小限のフィールドを合成する。
function synthesizeProblemDetail(status: number): ProblemDetail {
  return {
    type: "about:blank",
    title: `HTTP ${status}`,
    status,
    detail: "",
  };
}

function isProblemDetailLike(value: unknown): value is ProblemDetail {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.type === "string" &&
    typeof v.title === "string" &&
    typeof v.status === "number" &&
    typeof v.detail === "string"
  );
}

async function parseProblemDetail(
  response: Response,
): Promise<ProblemDetail> {
  // Content-Type は application/problem+json と application/json の両方を許容する。
  // それ以外の場合や JSON パースに失敗した場合は最小限のフォールバックを返す。
  const contentType = response.headers.get("content-type") ?? "";
  const isJsonLike =
    contentType.includes("application/problem+json") ||
    contentType.includes("application/json");

  if (!isJsonLike) {
    return synthesizeProblemDetail(response.status);
  }

  try {
    // body は middleware から下流へも渡るため、必ず clone してから読む。
    const cloned = response.clone();
    const json: unknown = await cloned.json();
    if (isProblemDetailLike(json)) {
      return json;
    }
    // JSON ではあるが ProblemDetail 形状でない場合は status を維持して合成する。
    return synthesizeProblemDetail(response.status);
  } catch {
    return synthesizeProblemDetail(response.status);
  }
}

const errorMiddleware: Middleware = {
  async onResponse({ response }) {
    if (response.ok) return undefined;
    const problem = await parseProblemDetail(response);
    throw new ApiError(response.status, problem);
  },
};

// トークン使用量のイベント通知
export type TokenUsageEvent = {
  inputTokens: number;
  outputTokens: number;
  model: string;
};

type TokenUsageListener = (usage: TokenUsageEvent) => void;
const tokenListeners: Set<TokenUsageListener> = new Set();

export function onTokenUsage(listener: TokenUsageListener): () => void {
  tokenListeners.add(listener);
  return () => tokenListeners.delete(listener);
}

const tokenCaptureMiddleware: Middleware = {
  async onResponse({ response }) {
    const input = response.headers.get("x-token-input");
    const output = response.headers.get("x-token-output");
    if (input && output) {
      const usage: TokenUsageEvent = {
        inputTokens: Number.parseInt(input, 10),
        outputTokens: Number.parseInt(output, 10),
        model: response.headers.get("x-model") ?? "unknown",
      };
      for (const listener of tokenListeners) listener(usage);
    }
    return undefined;
  },
};

// プロンプトサマリーのイベント通知
// ヘッダ値はサーバー側で UTF-8 Base64 エンコードされているため、ここでデコードする。
type PromptSummaryListener = (summary: string) => void;
const promptListeners: Set<PromptSummaryListener> = new Set();

export function onPromptSummary(listener: PromptSummaryListener): () => void {
  promptListeners.add(listener);
  return () => promptListeners.delete(listener);
}

export function offPromptSummary(listener: PromptSummaryListener): void {
  promptListeners.delete(listener);
}

function decodeBase64Utf8(encoded: string): string {
  // atob で Base64 → バイト文字列に戻し、TextDecoder で UTF-8 として解釈する。
  const bytes = Uint8Array.from(atob(encoded), (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

const promptCaptureMiddleware: Middleware = {
  async onResponse({ response }) {
    const encoded = response.headers.get("x-prompt-summary");
    if (encoded) {
      const summary = decodeBase64Utf8(encoded);
      for (const listener of promptListeners) listener(summary);
    }
    return undefined;
  },
};

export type ApiClient = ReturnType<typeof createClient<paths>>;

export function createApiClient(baseUrl: string): ApiClient {
  const client = createClient<paths>({ baseUrl });
  client.use(tokenCaptureMiddleware);
  client.use(promptCaptureMiddleware);
  client.use(errorMiddleware);
  return client;
}
